// Pin/badge artwork lives at a remote URL (badge.link). To draw it into a
// sticker we serialize an <svg> containing an <image> and rasterize it onto a
// canvas (see svgToPng.ts / composite.ts). A canvas that has drawn a *remote*
// image is "tainted" and canvas.toDataURL() throws a SecurityError — so the
// artwork MUST be embedded as a data: URL before it goes into the SVG, exactly
// like the font is embedded in embedFont.ts.
//
// On native (the real Instagram-share target) fetch() reaches the CDN without
// CORS restrictions, so embedding always succeeds. On web a cross-origin badge
// host without CORS headers will fail the fetch — callers treat a null result
// as "fall back to a text-only medallion", so the editor still renders.

const cache = new Map<string, Promise<string | null>>();

// Fetch a remote image and return a data: URL, or null if it can't be embedded.
// Results are cached per-URL for the session; a failed fetch is not cached so a
// later attempt (e.g. after coming back online) can retry.
export async function imageUrlToDataUrl(url: string): Promise<string | null> {
    if (!url) return null;
    if (url.startsWith("data:")) return url;

    const cached = cache.get(url);
    if (cached) return cached;

    const promise = (async () => {
        try {
            const res = await fetch(url);
            if (!res.ok) throw new Error(`image fetch failed: ${res.status}`);
            const blob = await res.blob();
            return await blobToDataUrl(blob);
        } catch (e) {
            cache.delete(url);
            console.warn("Pin artwork embed failed; using text fallback", url, e);
            return null;
        }
    })();

    cache.set(url, promise);
    return promise;
}

function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error || new Error("blobToDataUrl failed"));
        reader.readAsDataURL(blob);
    });
}
